import { Subject } from 'rxjs';
import type { ValidationResponseMessage } from '../../@types/common';
import type { RecordResult } from '../../@types/records';
import { recordApi } from '../../redux/services/record';
import { appDispatch } from '../../redux/store-accessors';
import { BackgroundJobHandler } from './background-job.handler';

export const saveRecordSuccessCallbackMap = new Map<string, Subject<number>>();
export const saveRecordData = new Map<string, Omit<RecordResult, 'id'>>();

export class SaveRecordBackgroundJobHandler extends BackgroundJobHandler<Omit<RecordResult, 'id'>> {
  constructor(apiProps: Omit<RecordResult, 'id'>, details?: Record<string, unknown>) {
    super(apiProps, details);
  }

  public createBackgroundJob() {
    const jobId = String(this.details?.jobId ?? '');
    const recordData = saveRecordData.get(jobId);
    if (!recordData) {
      return Promise.reject(new Error(`No record data found for job ID: ${jobId}`));
    }

    return appDispatch(recordApi.endpoints.saveRecordBackgroundJob.initiate(recordData)).unwrap();
  }

  public getJobDetails(backgroundJobId: number) {
    const subscription = appDispatch(
      recordApi.endpoints.getMutateRecordBackgroundJobDetails.initiate(backgroundJobId, { forceRefetch: true })
    );
    return subscription.unwrap().finally(() => subscription.unsubscribe());
  }

  public successCallback({ jobDetails }: Record<string, string>) {
    return new Promise<void>((resolve) => {
      const recordMutationSubject = saveRecordSuccessCallbackMap.get(String(this.details?.jobId ?? ''));

      if (recordMutationSubject && !recordMutationSubject.closed) {
        // For the record save process to be completed
        recordMutationSubject.next(Number(jobDetails));
        recordMutationSubject.complete();

        appDispatch(
          recordApi.util.invalidateTags([
            { type: 'Record', id: 'LIST' },
            'TotalRecordCount',
            'AssignedRecordsCount',
            'AllFormsRecord',
          ])
        );
      }

      saveRecordSuccessCallbackMap.delete(String(this.details?.jobId ?? ''));
      saveRecordData.delete(String(this.details?.jobId ?? ''));
      resolve();
    });
  }

  errorCallback(responseErrors: ValidationResponseMessage[]): void {
    const recordMutationSubject = saveRecordSuccessCallbackMap.get(String(this.details?.jobId ?? ''));
    if (recordMutationSubject && !recordMutationSubject.closed) {
      // For the record save process to be completed and show an error
      recordMutationSubject.error({ data: { validation: { responseErrors } }, status: 400 });
    }

    saveRecordSuccessCallbackMap.delete(String(this.details?.jobId ?? ''));
  }
}
